import { z } from "zod";
import {
  CredentialSummarySchema,
  FeatureFlagsSchema,
  NotificationPolicySchema,
  SettingsSchema,
  SnapshotScheduleSchema,
} from "../entities";
import { CredentialKindSchema, VerifyStatusSchema } from "../enums";
import { CurrencySchema } from "../branded";
import { JsonObjectSchema } from "../primitives";

/** `GET /api/settings` — settings plus derived feature gates. */
export const GetSettingsResponseSchema = SettingsSchema.extend({
  features: FeatureFlagsSchema,
});
export type GetSettingsResponse = z.infer<typeof GetSettingsResponseSchema>;

/** `PUT /api/settings` — mutable non-secret settings (all optional/partial). */
export const UpdateSettingsSchema = z
  .object({
    timezone: z.string(),
    baseCurrency: CurrencySchema,
    tradingArmed: z.boolean(),
    defaultDryRun: z.boolean(),
    notifPolicy: NotificationPolicySchema,
    snapshotSchedule: SnapshotScheduleSchema,
  })
  .partial();
export type UpdateSettings = z.infer<typeof UpdateSettingsSchema>;

/** `GET /api/settings/credentials` — masked list. */
export const ListCredentialsResponseSchema = z.object({
  credentials: z.array(CredentialSummarySchema),
});
export type ListCredentialsResponse = z.infer<typeof ListCredentialsResponseSchema>;

/** `:kind` path param for credential routes. */
export const CredentialKindParamSchema = z.object({ kind: CredentialKindSchema });
export type CredentialKindParam = z.infer<typeof CredentialKindParamSchema>;

/**
 * `PUT /api/settings/credentials/:kind` — the plaintext secret to encrypt.
 * Discriminated on `authType`; the server encrypts and only ever returns the
 * masked `CredentialSummary`.
 */
export const PutCredentialSchema = z.discriminatedUnion("authType", [
  z.object({
    authType: z.literal("api_key"),
    // `.trim()` throughout: pasted keys routinely carry trailing newlines/spaces,
    // and upstream OAuth servers reject the padded value with opaque errors
    // (e.g. Toss `unidentified-client`) — normalize at the boundary.
    apiKey: z.string().trim().min(1),
    /**
     * OpenAI-compatible endpoint base URL. Used **only** by the `ai_custom` kind
     * (OpenRouter / Ollama / LM Studio / vLLM); other kinds ignore it. Required
     * for `ai_custom` — the server rejects an `ai_custom` PUT without it (§6).
     */
    baseUrl: z.string().trim().url().optional(),
    meta: JsonObjectSchema.optional(),
  }),
  z.object({
    authType: z.literal("oauth_bearer"),
    token: z.string().trim().min(1),
    betaHeader: z.string().optional(),
    meta: JsonObjectSchema.optional(),
  }),
  z.object({
    authType: z.literal("client_secret"),
    clientId: z.string().trim().min(1),
    clientSecret: z.string().trim().min(1),
    meta: JsonObjectSchema.optional(),
  }),
]);
export type PutCredential = z.infer<typeof PutCredentialSchema>;

/** `POST /api/settings/credentials/:kind/test` — verify + unlock gate. */
export const TestCredentialResponseSchema = z.object({
  ok: z.boolean(),
  verifyStatus: VerifyStatusSchema,
  message: z.string().optional(),
  features: FeatureFlagsSchema,
});
export type TestCredentialResponse = z.infer<typeof TestCredentialResponseSchema>;

// ── model-alias overrides (`/api/settings/ai-aliases`) ───────────────────────

/** The stable internal model roles the AI layer resolves (mirrors `@richrich/ai`). */
export const ModelAliasSchema = z.enum(["chat-builder", "advisor", "classify"]);
export type ModelAlias = z.infer<typeof ModelAliasSchema>;

/** Providers a stored alias ref may target (mirrors the built-in AI factories). */
export const AI_ALIAS_PROVIDERS = ["openai", "anthropic", "custom"] as const;

/**
 * A registry reference `provider:modelId` (e.g. `anthropic:claude-opus-4-8`,
 * `custom:deepseek/deepseek-chat`). Shape-validated only: the provider must be a
 * known built-in, but whether it is *credentialed* is deliberately NOT checked
 * here — an alias pointing at an uncredentialed provider is allowed to persist so
 * the runtime fails **loud** at resolution rather than silently falling back (§6).
 */
export const ModelRefSchema = z
  .string()
  .trim()
  .regex(/^[a-z0-9_-]+:.+$/i, 'expected a "provider:modelId" reference')
  .refine(
    (ref) =>
      (AI_ALIAS_PROVIDERS as readonly string[]).includes(
        ref.slice(0, ref.indexOf(":")).toLowerCase(),
      ),
    { message: `provider must be one of: ${AI_ALIAS_PROVIDERS.join(", ")}` },
  );

/** A full alias → ref map (every role present). */
export const AiAliasMapSchema = z.object({
  "chat-builder": ModelRefSchema,
  advisor: ModelRefSchema,
  classify: ModelRefSchema,
});
export type AiAliasMap = z.infer<typeof AiAliasMapSchema>;

/** `GET /api/settings/ai-aliases` — effective (stored-over-default) refs + the defaults. */
export const GetAiAliasesResponseSchema = z.object({
  aliases: AiAliasMapSchema,
  defaults: AiAliasMapSchema,
});
export type GetAiAliasesResponse = z.infer<typeof GetAiAliasesResponseSchema>;

/** `PUT /api/settings/ai-aliases` — partial override (only the roles supplied). */
export const PutAiAliasesSchema = AiAliasMapSchema.partial();
export type PutAiAliases = z.infer<typeof PutAiAliasesSchema>;

// ── agent tool toggles (`/api/settings/agent-tools`) ─────────────────────────

/** The tool-bearing agents whose tool sets the operator can manage. */
export const AgentIdSchema = z.enum(["chat", "advisor"]);
export type AgentId = z.infer<typeof AgentIdSchema>;

/**
 * One tool as the management surface sees it. `name` is the model-facing slot id
 * (SSOT: the `@richrich/ai` catalogs — deliberately NOT enumerated here so a new
 * tool never needs a contracts change); `core` tools cannot be disabled; `tier`
 * mirrors the chat registry's exposure tier (advisor tools are all `built_in`).
 */
export const AgentToolInfoSchema = z.object({
  name: z.string(),
  tier: z.enum(["built_in", "lazy_load"]),
  core: z.boolean(),
  enabled: z.boolean(),
});
export type AgentToolInfo = z.infer<typeof AgentToolInfoSchema>;

/** `GET /api/settings/agent-tools` — the full per-agent tool catalog + state. */
export const GetAgentToolsResponseSchema = z.object({
  chat: z.array(AgentToolInfoSchema),
  advisor: z.array(AgentToolInfoSchema),
});
export type GetAgentToolsResponse = z.infer<typeof GetAgentToolsResponseSchema>;

/**
 * `PUT /api/settings/agent-tools` — per agent, the FULL replacement disabled-set
 * (an agent omitted is left untouched). Names are shape-validated only; the server
 * rejects unknown or core names against the live `@richrich/ai` catalogs.
 */
export const PutAgentToolsSchema = z
  .object({
    chat: z.object({ disabled: z.array(z.string().trim().min(1)) }),
    advisor: z.object({ disabled: z.array(z.string().trim().min(1)) }),
  })
  .partial();
export type PutAgentTools = z.infer<typeof PutAgentToolsSchema>;

// ── available models per provider (`/api/settings/ai-models`) ────────────────

/** One selectable model in the alias picker (id is the registry `modelId`). */
export const AiModelOptionSchema = z.object({
  id: z.string(),
  label: z.string(),
});
export type AiModelOption = z.infer<typeof AiModelOptionSchema>;

/**
 * The models available for one registered provider. `registered:false` providers
 * are still returned (empty models) so the UI can grey them out; `error` is set
 * (with empty `models`) when a provider's live model list could not be fetched —
 * the endpoint is fail-soft and never throws for a single provider (§6).
 */
export const AiModelProviderSchema = z.object({
  provider: z.enum(AI_ALIAS_PROVIDERS),
  registered: z.boolean(),
  models: z.array(AiModelOptionSchema),
  error: z.string().optional(),
});
export type AiModelProvider = z.infer<typeof AiModelProviderSchema>;

/** `GET /api/settings/ai-models` — per-provider model availability for the picker. */
export const GetAiModelsResponseSchema = z.object({
  providers: z.array(AiModelProviderSchema),
});
export type GetAiModelsResponse = z.infer<typeof GetAiModelsResponseSchema>;
